import {
  CANTEEN_SCHEDULE_PERIOD_CLOCK,
  CanteenAccount,
  CanteenMenuSlot,
  CanteenOrder,
  CanteenOrderStatus,
  CanteenPaymentMethod,
  CanteenProduct,
  CanteenPromotion,
  CanteenRole,
  CanteenSchedulePeriod,
  CanteenStudent,
  CanteenTransaction,
  CanteenUnit,
  CanteenWeekday,
  roundMoney,
  toIsoDate,
} from 'core/models/canteen.model';

const WEEKDAY_BY_INDEX: readonly CanteenWeekday[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export interface PricedPurchaseLine {
  readonly product: CanteenProduct;
  readonly slot: CanteenMenuSlot | null;
  readonly quantity: number;
  readonly unitPrice: number;
}

export interface PurchaseCheckContext {
  readonly now: Date;
  readonly scheduledFor: Date | null;
  readonly paymentMethod: CanteenPaymentMethod;
  readonly channel: 'APP' | 'POS';
  readonly student: CanteenStudent | null;
  readonly account: CanteenAccount | null;
  readonly canteen: CanteenUnit;
  readonly cashierOpen: boolean;
  readonly lines: readonly PricedPurchaseLine[];
  readonly blockedProductIds: ReadonlySet<string>;
  readonly restrictedIngredients: readonly string[];
  readonly spend: {
    readonly daily: number;
    readonly weekly: number;
    readonly monthly: number;
    readonly total: number;
  };
}

export function weekdayOf(date: Date): CanteenWeekday {
  return WEEKDAY_BY_INDEX[date.getDay()] ?? 'monday';
}

export function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function minutesOfClock(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function clockInRange(
  date: Date,
  startsAt: string,
  endsAt: string,
): boolean {
  const minutes = minutesOfDay(date);
  return (
    minutes >= minutesOfClock(startsAt) && minutes <= minutesOfClock(endsAt)
  );
}

const SCHOOL_WEEKDAYS = new Set<CanteenWeekday>([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
]);

export function nextSchedulePeriodAt(
  period: CanteenSchedulePeriod,
  now: Date,
  advanceHours: number,
): Date {
  const [hours, minutes] = CANTEEN_SCHEDULE_PERIOD_CLOCK[period]
    .split(':')
    .map(Number);
  const earliest = now.getTime() + advanceHours * 60 * 60 * 1000;

  for (let offset = 0; offset < 14; offset += 1) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + offset);
    candidate.setHours(hours ?? 0, minutes ?? 0, 0, 0);
    if (
      SCHOOL_WEEKDAYS.has(weekdayOf(candidate)) &&
      candidate.getTime() >= earliest
    ) {
      return candidate;
    }
  }

  const fallback = new Date(now);
  fallback.setDate(now.getDate() + 1);
  fallback.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return fallback;
}

export function dateInRange(
  date: Date,
  validFrom: string,
  validUntil: string | null,
): boolean {
  const day = toIsoDate(date);
  if (day < validFrom) {
    return false;
  }
  return validUntil === null || day <= validUntil;
}

export function slotMatches(slot: CanteenMenuSlot, at: Date): boolean {
  return (
    slot.weekdays.includes(weekdayOf(at)) &&
    clockInRange(at, slot.startsAt, slot.endsAt) &&
    dateInRange(at, slot.validFrom, slot.validUntil)
  );
}

export function activePromotion(
  promotions: readonly CanteenPromotion[],
  productId: string,
  at: Date,
): CanteenPromotion | null {
  return (
    promotions.find(
      (item) =>
        item.active &&
        item.productId === productId &&
        dateInRange(at, item.validFrom, item.validUntil),
    ) ?? null
  );
}

export function resolveUnitPrice(
  product: CanteenProduct,
  slot: CanteenMenuSlot | null,
  promotion: CanteenPromotion | null,
): number {
  let price = slot?.price ?? product.price;
  if (!promotion) {
    return roundMoney(price);
  }

  if (promotion.kind === 'PERCENT') {
    price = price * (1 - promotion.value / 100);
  } else if (promotion.kind === 'FIXED') {
    price = Math.max(0, price - promotion.value);
  } else {
    price = promotion.value;
  }

  return roundMoney(price);
}

export function lineTotal(unitPrice: number, quantity: number): number {
  return roundMoney(unitPrice * quantity);
}

function ingredientBlocked(
  product: CanteenProduct,
  restricted: readonly string[],
): boolean {
  const tokens = [...product.ingredients, ...product.allergens].map((item) =>
    item.toLocaleLowerCase('pt-BR'),
  );
  return restricted.some((item) =>
    tokens.includes(item.toLocaleLowerCase('pt-BR')),
  );
}

export function validatePurchase(context: PurchaseCheckContext): string[] {
  const reasons: string[] = [];
  const scheduling = context.scheduledFor !== null;
  const moment = context.scheduledFor ?? context.now;

  if (!context.student || context.student.status !== 'ACTIVE') {
    reasons.push('Aluno inativo.');
  }

  if (!context.account) {
    reasons.push('Conta de consumo não encontrada.');
  } else if (context.account.status !== 'ACTIVE') {
    reasons.push('Conta bloqueada ou suspensa.');
  } else if (
    context.student &&
    (context.account.studentId !== context.student.id ||
      context.account.schoolId !== context.student.schoolId)
  ) {
    reasons.push('Conta não pertence a este aluno.');
  }

  if (context.canteen.status !== 'ACTIVE') {
    reasons.push('Cantina indisponível.');
  }

  if (scheduling && !context.canteen.allowsScheduling) {
    reasons.push('Agendamento desabilitado.');
  }

  if (!scheduling && !context.canteen.allowsSale) {
    reasons.push('Venda desabilitada.');
  }

  if (context.channel === 'POS' && !context.canteen.allowsCounterSale) {
    reasons.push('Venda presencial desabilitada.');
  }

  if (context.channel === 'POS' && !context.cashierOpen) {
    reasons.push('Caixa fechado.');
  }

  if (
    context.scheduledFor &&
    context.scheduledFor.getTime() - context.now.getTime() <
      context.canteen.minimumSchedulingAdvanceHours * 60 * 60 * 1000
  ) {
    reasons.push('Agendamento não permitido para este horário.');
  }

  if (
    !clockInRange(moment, context.canteen.opensAt, context.canteen.closesAt)
  ) {
    reasons.push('Fora do horário de funcionamento.');
  }

  if (context.lines.length === 0) {
    reasons.push('Selecione ao menos um produto.');
  }

  let total = 0;
  for (const line of context.lines) {
    const product = line.product;
    total = roundMoney(total + lineTotal(line.unitPrice, line.quantity));

    if (!product.active) {
      reasons.push(`${product.name} está inativo.`);
    }

    if (!scheduling && !product.availableForSale) {
      reasons.push(`${product.name} indisponível para venda.`);
    }

    if (scheduling && !product.availableForScheduling) {
      reasons.push(`${product.name} indisponível para agendamento.`);
    }

    if (!line.slot || !slotMatches(line.slot, moment)) {
      reasons.push(`${product.name} fora do horário do cardápio.`);
    } else if (scheduling && !line.slot.availableForScheduling) {
      reasons.push(`${product.name} fora da agenda do cardápio.`);
    }

    if (product.stockControlled && product.stock < line.quantity) {
      reasons.push(`${product.name} sem estoque.`);
    }

    if (context.blockedProductIds.has(product.id)) {
      reasons.push(`Produto bloqueado para este aluno: ${product.name}.`);
    }

    if (ingredientBlocked(product, context.restrictedIngredients)) {
      reasons.push(`${product.name} conflita com restrição alimentar.`);
    }
  }

  const account = context.account;
  if (account) {
    if (account.orderLimit !== null && total > account.orderLimit) {
      reasons.push('Pedido bloqueado. Motivo: limite por pedido atingido.');
    }
    if (
      account.dailyLimit !== null &&
      context.spend.daily + total > account.dailyLimit
    ) {
      reasons.push('Pedido bloqueado. Motivo: limite de consumo atingido.');
    }
    if (
      account.weeklyLimit !== null &&
      context.spend.weekly + total > account.weeklyLimit
    ) {
      reasons.push('Pedido bloqueado. Motivo: limite semanal atingido.');
    }
    if (
      account.monthlyLimit !== null &&
      context.spend.monthly + total > account.monthlyLimit
    ) {
      reasons.push('Pedido bloqueado. Motivo: limite mensal atingido.');
    }
  }

  if (context.paymentMethod === 'BALANCE') {
    if (!context.canteen.allowsBalance) {
      reasons.push('Pagamento com saldo desabilitado.');
    } else if (account && total > account.balance) {
      reasons.push('Saldo insuficiente.');
    }
  }

  return reasons;
}

export function startOfWeek(date: Date): Date {
  const copy = new Date(date);
  const day = copy.getDay();
  const distance = day === 0 ? 6 : day - 1;
  copy.setDate(copy.getDate() - distance);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function consumptionSpend(
  transactions: readonly CanteenTransaction[],
  studentId: string,
  now: Date,
): { daily: number; weekly: number; monthly: number; total: number } {
  const dailyStart = startOfDay(now).getTime();
  const weekStart = startOfWeek(now).getTime();
  const monthStart = startOfMonth(now).getTime();
  const spend = { daily: 0, weekly: 0, monthly: 0, total: 0 };

  for (const item of transactions) {
    if (item.studentId !== studentId) {
      continue;
    }

    const at = new Date(item.occurredAt).getTime();
    const signed =
      item.type === 'DEBIT'
        ? item.amount
        : item.type === 'REFUND' || item.type === 'CANCELLATION'
          ? -item.amount
          : 0;
    if (signed === 0) {
      continue;
    }

    spend.total = roundMoney(spend.total + signed);
    if (at >= monthStart) {
      spend.monthly = roundMoney(spend.monthly + signed);
    }
    if (at >= weekStart) {
      spend.weekly = roundMoney(spend.weekly + signed);
    }
    if (at >= dailyStart) {
      spend.daily = roundMoney(spend.daily + signed);
    }
  }

  return spend;
}

const LOCKED_CANCEL: ReadonlySet<CanteenOrderStatus> = new Set([
  'CANCELLED',
  'REFUNDED',
  'DELIVERED',
]);

const ADMIN_CANCEL: ReadonlySet<CanteenOrderStatus> = new Set([
  'PREPARING',
  'READY',
  'READY_FOR_PICKUP',
]);

export function canCancelOrder(
  order: CanteenOrder,
  role: CanteenRole,
  now: Date,
  cancellationWindowHours: number,
): boolean {
  if (LOCKED_CANCEL.has(order.status)) {
    return false;
  }

  if (ADMIN_CANCEL.has(order.status)) {
    return role === 'CANTEEN_ADMIN';
  }

  if (order.scheduledFor && role !== 'CANTEEN_ADMIN') {
    const limit =
      new Date(order.scheduledFor).getTime() -
      cancellationWindowHours * 60 * 60 * 1000;
    if (now.getTime() > limit) {
      return false;
    }
  }

  return true;
}

const REFUNDABLE: ReadonlySet<CanteenOrderStatus> = new Set([
  'PAID',
  'SCHEDULED',
  'PREPARING',
  'READY',
  'READY_FOR_PICKUP',
  'DELIVERED',
]);

export function canRefundOrder(
  order: CanteenOrder,
  role: CanteenRole,
): boolean {
  return (
    REFUNDABLE.has(order.status) &&
    (role === 'CANTEEN_ADMIN' || role === 'CANTEEN_MANAGER')
  );
}

export function canPickupOrder(order: CanteenOrder): boolean {
  return order.status === 'READY_FOR_PICKUP' || order.status === 'READY';
}

export function expectedCashierBalance(input: {
  readonly openingBalance: number;
  readonly entries: number;
  readonly sales: number;
  readonly refunds: number;
  readonly withdrawals: number;
  readonly adjustments: number;
}): number {
  return roundMoney(
    input.openingBalance +
      input.entries +
      input.sales -
      input.refunds -
      input.withdrawals +
      input.adjustments,
  );
}
